import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';
import { User } from '../types';
import { API_URL } from '../config';
import {
  beginMicrosoftLogin,
  beginMicrosoftLogout,
  isMicrosoftSsoConfigured,
} from '../auth/azureAuth';
import {
  clearAuthSession,
  getAuthProvider,
  getAuthToken,
  setAuthProvider,
  setAuthToken,
} from '../utils/authToken';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<{ must_change_password: boolean }>;
  loginWithSsoIdToken: (idToken: string) => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  logout: () => Promise<void>;
  /** Recarrega o utilizador a partir de `/api/auth/me` (ex.: após atualizar preferências). */
  refreshUser: () => Promise<void>;
  isAuthenticated: boolean;
  isMicrosoftSsoEnabled: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type LoginErrorDetail = {
  message?: string;
  code?: string;
  error_id?: string;
  diagnostic?: string;
};

const isLoginErrorDetail = (value: unknown): value is LoginErrorDetail => {
  if (!value || typeof value !== 'object') return false;
  return (
    'message' in value ||
    'code' in value ||
    'error_id' in value ||
    'diagnostic' in value
  );
};

// Configurar axios globalmente para usar UTF-8
axios.defaults.headers.common['Content-Type'] = 'application/json; charset=utf-8';
axios.defaults.responseType = 'json';
if (axios.defaults.transformResponse) {
  // Manter transformações existentes
} else {
  axios.defaults.transformResponse = [(data) => {
    if (typeof data === 'string') {
      try {
        return JSON.parse(data);
      } catch (e) {
        return data;
      }
    }
    return data;
  }];
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const requestInterceptorId = axios.interceptors.request.use((config) => {
      const storedToken = getAuthToken();
      const requestUrl = String(config.url || '');
      const isApiRequest = requestUrl.includes('/api/');
      const isAuthRequest =
        requestUrl.includes('/api/auth/login') ||
        requestUrl.includes('/api/auth/me') ||
        requestUrl.includes('/api/auth/sso/login');

      if (!storedToken && isApiRequest && !isAuthRequest) {
        return Promise.reject(new axios.Cancel('Missing auth token'));
      }

      const headers = (config.headers ?? {}) as any;
      const invalidAuthHeader =
        typeof headers.Authorization === 'string' &&
        (headers.Authorization.includes('Bearer null') ||
          headers.Authorization.includes('Bearer undefined'));

      if (invalidAuthHeader) {
        delete headers.Authorization;
      }

      if (storedToken && !headers.Authorization) {
        headers.Authorization = `Bearer ${storedToken}`;
      }

      config.headers = headers;
      return config;
    });

    const responseInterceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const statusCode = error.response?.status;
        const requestUrl = String(error.config?.url || '');
        const isAuthRequest =
          requestUrl.includes('/api/auth/login') ||
          requestUrl.includes('/api/auth/me') ||
          requestUrl.includes('/api/auth/sso/login');

        if (statusCode === 401 && !isAuthRequest) {
          clearAuthSession();
          setUser(null);
          setToken(null);
          delete axios.defaults.headers.common['Authorization'];

          // Não redirecionar se já estivermos na página de login ou na página de callback do SSO
          const isAuthPage = 
            window.location.pathname === '/login' || 
            window.location.pathname === '/auth/callback';

          if (!isAuthPage) {
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  useEffect(() => {
    const storedToken = getAuthToken();
    if (storedToken) {
      setToken(storedToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);
 
  const fetchUser = async (): Promise<boolean> => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        timeout: 30000, // 30 segundos
      });
      setUser(response.data);
      return true;
    } catch (error: any) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      console.error('Erro ao carregar usuario autenticado (/api/auth/me):', {
        status,
        detail,
      });
      clearAuthSession();
      setToken(null);
      delete axios.defaults.headers.common['Authorization'];
      return false;
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = useCallback(async () => {
    const storedToken = getAuthToken();
    if (!storedToken) return;
    try {
      const response = await axios.get<User>(`${API_URL}/api/auth/me`, {
        timeout: 30000,
      });
      setUser(response.data);
    } catch (error) {
      console.error('Erro ao atualizar utilizador (/api/auth/me):', error);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        timeout: 30000, // 30 segundos
      });

      const { access_token, must_change_password } = response.data;
      setToken(access_token);
      setAuthToken(access_token);
      setAuthProvider('local');
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      const userLoaded = await fetchUser();
      if (!userLoaded) {
        throw new Error('Login efetuado, mas nao foi possivel carregar o usuario autenticado.');
      }
      
      // Retornar informação sobre necessidade de trocar senha
      return { must_change_password: must_change_password || false };
    } catch (error: any) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Timeout ao conectar com o servidor. Verifique sua conexão e a URL da API.');
      }
      if (error.code === 'ECONNREFUSED' || error.response === undefined) {
        throw new Error('Não foi possível conectar ao servidor. Verifique se o backend está rodando.');
      }
      if (axios.isAxiosError(error)) {
        const statusCode = error.response?.status;
        const rawDetail = error.response?.data?.detail;
        const backendDetail =
          typeof rawDetail === 'string'
            ? rawDetail
            : isLoginErrorDetail(rawDetail)
            ? rawDetail.message || null
            : null;
        const errorCode =
          isLoginErrorDetail(rawDetail) && rawDetail.code ? rawDetail.code : null;
        const errorId =
          isLoginErrorDetail(rawDetail) && rawDetail.error_id ? rawDetail.error_id : null;
        const diagnostic =
          isLoginErrorDetail(rawDetail) && rawDetail.diagnostic ? rawDetail.diagnostic : null;
        const supportInfo =
          errorCode || errorId
            ? ` [Codigo: ${[errorCode, errorId].filter(Boolean).join(' / ')}]`
            : '';

        if (statusCode === 401) {
          throw new Error('E-mail ou senha inválidos. Confira seus dados e tente novamente.');
        }

        if (statusCode && statusCode >= 500) {
          throw new Error(
            `Nao foi possivel fazer login agora devido a uma instabilidade no servidor. Tente novamente em alguns minutos.${supportInfo}${
              diagnostic ? ` (${diagnostic})` : ''
            }`
          );
        }

        if (backendDetail) {
          throw new Error(backendDetail);
        }
      }
      throw error;
    }
  };

  const loginWithSsoIdToken = async (idToken: string) => {
    const response = await axios.post(
      `${API_URL}/api/auth/sso/login`,
      { id_token: idToken },
      {
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 30000,
      }
    );

    const { access_token } = response.data;
    setToken(access_token);
    setAuthToken(access_token);
    setAuthProvider('sso_microsoft');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    const userLoaded = await fetchUser();
    if (!userLoaded) {
      throw new Error('SSO validado, mas nao foi possivel carregar o usuario autenticado. Verifique /api/auth/me no ambiente.');
    }
  };

  const loginWithMicrosoft = async () => {
    await beginMicrosoftLogin();
  };

  const logout = async () => {
    const authProvider = getAuthProvider();
    setUser(null);
    setToken(null);
    clearAuthSession();
    delete axios.defaults.headers.common['Authorization'];

    if (authProvider === 'sso_microsoft') {
      await beginMicrosoftLogout();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        loginWithSsoIdToken,
        loginWithMicrosoft,
        logout,
        refreshUser,
        isAuthenticated: !!token,
        isMicrosoftSsoEnabled: isMicrosoftSsoConfigured(),
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

